import { useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const REASONS = [["too_long", "Too long"], ["robotic", "Sounds robotic"], ["not_helpful", "Not helpful"], ["wrong", "Got something wrong"]];
const LABEL = { short: "Got it: short replies from now on. Change it any time under You.", detailed: "Got it: more detailed replies from now on. Change it any time under You.", balanced: "Back to balanced replies." };

export async function saveLength(length, setUser) {
  await api.put("/v2/chat/style", { length });
  setUser?.((u) => (u ? { ...u, chat_length: length } : u));
  toast.success(LABEL[length]);
}

export function StyleOffer({ offer, onDone }) {
  const { setUser } = useAuth();
  if (!offer) return null;
  const yes = async () => { try { await saveLength(offer.length, setUser); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="style-offer">
      <span className="text-stone-600">{offer.label}</span>
      <button onClick={yes} data-testid="style-offer-yes" className="px-4 h-11 rounded-full bg-stone-900 text-white font-semibold">Yes</button>
      <button onClick={onDone} data-testid="style-offer-no" className="px-4 h-11 rounded-full border border-stone-300 text-stone-700">No thanks</button>
    </div>
  );
}

export function RateBar({ m, i, onChange, onSuggest }) {
  const [asking, setAsking] = useState(false);
  const send = async (rating, reason = null) => {
    try {
      const { data } = await api.post(`/v2/chat/messages/${m.id}/rating`, { rating, reason });
      onChange(data.rating);
      setAsking(rating === "down" && !reason);
      if (reason) toast.success("Thanks, CAMRYN will use that.");
      if (data.suggest_length) onSuggest({ length: data.suggest_length, label: "Keep replies short from now on?" });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const btn = (kind, Icon) => (
    <button onClick={() => send(m.rating === kind ? "none" : kind)}
      data-testid={`rate-${kind}-${i}`} aria-label={kind === "up" ? "Helpful" : "Not helpful"} aria-pressed={m.rating === kind}
      className={`h-11 w-11 grid place-items-center rounded-full ${m.rating === kind ? "bg-stone-900 text-white" : "text-stone-400 hover:text-stone-700"}`}>
      <Icon className="h-3.5 w-3.5" /></button>
  );
  return (
    <div className="mt-1" data-testid={`rate-bar-${i}`}>
      <div className="flex gap-1">{btn("up", ThumbsUp)}{btn("down", ThumbsDown)}</div>
      {asking && (
        <div className="flex flex-wrap gap-1.5 mt-1" data-testid={`rate-reasons-${i}`}>
          {REASONS.map(([k, l]) => (
            <button key={k} onClick={() => send("down", k)} data-testid={`rate-reason-${k}-${i}`}
              className="px-4 h-11 rounded-full border border-stone-300 text-xs text-stone-700 hover:bg-stone-100">{l}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export function LengthSetting() {
  const { user, setUser } = useAuth();
  const cur = user?.chat_length || "balanced";
  const set = async (k) => { if (k !== cur) { try { await saveLength(k, setUser); } catch (e) { toast.error(errMsg(e)); } } };
  return (
    <div className="mt-5" data-testid="length-setting">
      <div className="text-sm font-medium">Reply length</div>
      <div className="grid grid-cols-3 gap-2 mt-2">
        {[["short", "Short"], ["balanced", "Balanced"], ["detailed", "Detailed"]].map(([k, l]) => (
          <button key={k} onClick={() => set(k)} data-testid={`length-${k}`}
            className={`h-11 rounded-full border text-sm font-semibold ${cur === k ? "border-stone-900 bg-stone-900 text-white" : "border-stone-200 text-stone-700"}`}>{l}</button>
        ))}
      </div>
    </div>
  );
}
